/**
 * AgentBadge.tsx
 *
 * Tiny "this wallet is an AI agent" indicator (Phase 2 / slice 1).
 *
 * Usage:
 *   <AgentBadge wallet={wallet} />          // compact pill, hover=tooltip
 *   <AgentBadge wallet={wallet} compact />  // smaller variant for list rows
 *
 * Behaviour
 * ---------
 *   - Renders nothing while the lookup is in flight (no flicker on mount).
 *   - Renders nothing when the wallet is not registered as an agent.
 *   - Renders nothing for revoked agents (so badges naturally disappear
 *     once an operator revokes — no manual UI cleanup needed).
 *   - The result is cached process-wide via `useAgentInfo` so a single
 *     wallet only triggers one network round-trip even if many cards
 *     render the same address.
 *
 * The badge is intentionally non-clickable for now.  Slice 1c's
 * AgentTab will let operators drill into capabilities/policy from
 * inside `ProfileOverlay`; the live-feed and map markers just need
 * to communicate "this is a bot" at a glance.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  probeAgent,
  type AgentRecord,
} from '../services/agentService';

// ── Shared cache (per page session) ────────────────────────────────────────
//
// Same pattern as the in-flight profile cache used by LiveDirectoryOverlay:
// keyed by wallet, with a small TTL so revocations propagate without a hard
// refresh.  All cache reads/writes go through `useAgentInfo`.

interface CacheEntry {
  rec: AgentRecord | null;
  fetchedAt: number;
}

const CACHE_TTL_MS = 60_000;
const cache: Map<string, CacheEntry> = new Map();
const inflight: Map<string, Promise<AgentRecord | null>> = new Map();

function getCached(wallet: string): AgentRecord | null | undefined {
  const entry = cache.get(wallet);
  if (!entry) return undefined;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    cache.delete(wallet);
    return undefined;
  }
  return entry.rec;
}

async function loadAgent(wallet: string): Promise<AgentRecord | null> {
  const cached = getCached(wallet);
  if (cached !== undefined) return cached;
  const existing = inflight.get(wallet);
  if (existing) return existing;
  const p = (async () => {
    try {
      const rec = await probeAgent(wallet);
      cache.set(wallet, { rec, fetchedAt: Date.now() });
      return rec;
    } finally {
      inflight.delete(wallet);
    }
  })();
  inflight.set(wallet, p);
  return p;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export interface UseAgentInfoResult {
  loading: boolean;
  agent: AgentRecord | null;
  isAgent: boolean;
  isActive: boolean;
}

export function useAgentInfo(wallet: string | null | undefined): UseAgentInfoResult {
  const [agent, setAgent] = useState<AgentRecord | null>(() => {
    if (!wallet) return null;
    const cached = getCached(wallet);
    return cached ?? null;
  });
  const [loading, setLoading] = useState<boolean>(() => {
    if (!wallet) return false;
    return getCached(wallet) === undefined;
  });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!wallet) {
      setAgent(null);
      setLoading(false);
      return;
    }
    const cached = getCached(wallet);
    if (cached !== undefined) {
      setAgent(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadAgent(wallet)
      .then((rec) => {
        if (cancelledRef.current) return;
        setAgent(rec);
      })
      .catch(() => {
        if (cancelledRef.current) return;
        setAgent(null);
      })
      .finally(() => {
        if (cancelledRef.current) return;
        setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [wallet]);

  const isAgent = !!agent;
  const isActive = !!agent && agent.statusLabel === 'active';
  return { loading, agent, isAgent, isActive };
}

/**
 * Force-evict a wallet from the local cache.  Call this after the
 * operator UI submits a register / revoke / set_capabilities tx so
 * badges + tabs reflect the new state on the next render without
 * waiting out the TTL.
 */
export function invalidateAgentCache(wallet: string | null | undefined): void {
  if (!wallet) return;
  cache.delete(wallet);
}

// ── Badge component ────────────────────────────────────────────────────────

interface AgentBadgeProps {
  wallet: string | null | undefined;
  /** Smaller padding/font, suitable for live-feed cards or map popups. */
  compact?: boolean;
  /** Override the default tooltip / a11y label. */
  title?: string;
  /** Render even for revoked agents — typically only the operator UI wants this. */
  showRevoked?: boolean;
}

const baseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const compactStyle: React.CSSProperties = {
  ...baseStyle,
  padding: '1px 6px',
  fontSize: 10,
  letterSpacing: 0.3,
};

const revokedStyle: React.CSSProperties = {
  ...baseStyle,
  background: 'linear-gradient(135deg, #6b7280, #4b5563)',
  opacity: 0.85,
};

export const AgentBadge: React.FC<AgentBadgeProps> = ({
  wallet,
  compact = false,
  title,
  showRevoked = false,
}) => {
  const { agent, loading } = useAgentInfo(wallet);
  if (loading || !agent) return null;
  const revoked = agent.statusLabel === 'revoked';
  if (revoked && !showRevoked) return null;
  const labelText = revoked ? 'AGENT (revoked)' : 'AGENT';
  const kindLabel =
    agent.kindLabel && agent.kindLabel !== 'generic' ? ` · ${agent.kindLabel}` : '';
  const tooltip =
    title ||
    `${labelText}${kindLabel}${agent.operator ? ` · operator ${agent.operator.slice(0, 4)}…${agent.operator.slice(-4)}` : ''}`;
  const style = revoked ? revokedStyle : compact ? compactStyle : baseStyle;
  return (
    <span
      style={style}
      title={tooltip}
      aria-label={tooltip}
      role="img"
    >
      <span aria-hidden="true">🤖</span>
      <span>{compact ? 'BOT' : 'AGENT'}</span>
    </span>
  );
};

export default AgentBadge;
